import { marked, Slugger } from 'marked';
import katex from 'katex';
import { createHighlighterCore, type HighlighterCore } from 'shiki/core';
import { createOnigurumaEngine } from 'shiki/engine/oniguruma';
import imageSizes from 'virtual:image-sizes';
import type { BlogPost, BlogPostMeta, TableOfContentsItem } from './types';
import { parseFrontmatter } from './frontmatter';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

marked.use({
  mangle: false,
  async: false,
  breaks: true,
  gfm: true,
  extensions: [{
    name: 'math',
    level: 'inline',
    start(src) { return src.match(/(?<!\\)\$(?!\$)/)?.index; },
    tokenizer(src) {
      const match = src.match(/^(?<!\\)\$([^\n$]+?)\$/);
      if (!match) return;
      return {
        type: 'html',
        raw: match[0],
        text: katex.renderToString(match[1].trim(), {
          displayMode: false,
          throwOnError: false
        })
      };
    }
  }, {
    name: 'displayMath',
    level: 'block',
    start(src) { return src.match(/\$\$/)?.index; },
    tokenizer(src) {
      const match = src.match(/^\$\$([\s\S]+?)\$\$/);
      if (!match) return;
      const cleanMath = match[1].trim().replace(/\s+/g, ' ');
      return {
        type: 'html',
        raw: match[0],
        text: `<div class="math-block">${katex.renderToString(cleanMath, {
          displayMode: true,
          throwOnError: false
        })}</div>`
      };
    }
  }, {
    name: 'tweetPlaceholder',
    level: 'inline',
    start(src) { return src.match(/\{\{TWEET_/)?.index; },
    tokenizer(src) {
      const match = src.match(/^\{\{TWEET_(\d+)\}\}/);
      if (!match) return;
      return {
        type: 'html',
        raw: match[0],
        text: `<div class="tweet-placeholder" data-tweet-id="${match[1]}"></div>`
      };
    }
  }]
});

const blogModules = import.meta.glob<string>('/src/content/blog/*.md', {
  eager: true,
  query: '?raw',
  import: 'default'
});

// Grammars are referenced as loaders, not as bare `import(...)` calls: a bare
// call starts fetching the moment this module evaluates, which would pull every
// grammar on page load. The `shiki/bundle/*` entrypoints are avoided for the
// same reason at build time — they emit a chunk per language Shiki ships.
const LANG_LOADERS: Record<string, () => Promise<unknown>> = {
  javascript: () => import('@shikijs/langs/javascript'),
  typescript: () => import('@shikijs/langs/typescript'),
  python: () => import('@shikijs/langs/python'),
  java: () => import('@shikijs/langs/java'),
  c: () => import('@shikijs/langs/c'),
  cpp: () => import('@shikijs/langs/cpp'),
  bash: () => import('@shikijs/langs/bash'),
  json: () => import('@shikijs/langs/json'),
  yaml: () => import('@shikijs/langs/yaml'),
  markdown: () => import('@shikijs/langs/markdown'),
  html: () => import('@shikijs/langs/html'),
  css: () => import('@shikijs/langs/css'),
  sql: () => import('@shikijs/langs/sql')
};

const LANG_ALIASES: Record<string, string> = {
  js: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  'c++': 'cpp',
  md: 'markdown',
  yml: 'yaml'
};

// github-dark-default over github-dark: its comment colour clears 4.5:1.
const SHIKI_THEME = 'github-dark-default';

function resolveLang(language: string): string | null {
  const lang = language.toLowerCase();
  const resolved = LANG_ALIASES[lang] ?? lang;
  return resolved in LANG_LOADERS ? resolved : null;
}

// Cache the promise, not the resolved value: posts render concurrently, and
// caching only the result lets every in-flight caller build its own highlighter.
let highlighterPromise: Promise<HighlighterCore> | null = null;

function getShikiHighlighter(): Promise<HighlighterCore> {
  if (!highlighterPromise) {
    highlighterPromise = createHighlighterCore({
      themes: [import('@shikijs/themes/github-dark-default')],
      langs: [],
      engine: createOnigurumaEngine(import('shiki/wasm'))
    });
  }
  return highlighterPromise;
}

/**
 * marked's code renderer is synchronous, so every grammar the document needs
 * has to be registered before rendering starts.
 */
async function loadLanguagesFor(markdown: string, shiki: HighlighterCore): Promise<void> {
  const alreadyLoaded = new Set(shiki.getLoadedLanguages());
  const needed = new Set<string>();

  for (const [, fence] of markdown.matchAll(/^```([a-zA-Z0-9+#_-]+)/gm)) {
    const lang = resolveLang(fence);
    if (lang && !alreadyLoaded.has(lang)) needed.add(lang);
  }

  await Promise.all([...needed].map(async lang => {
    try {
      await shiki.loadLanguage(LANG_LOADERS[lang]() as never);
    } catch (error) {
      console.error(`Failed to load the "${lang}" grammar:`, error);
    }
  }));
}

function renderCodeBlock(lang: string, code: string, body: string): string {
  return `
    <div class="code-block-container" data-language="${escapeHtml(lang)}">
      <div class="code-block-header">
        <span class="code-block-language">${escapeHtml(lang)}</span>
        <button class="code-block-copy" data-code="${escapeHtml(code)}" aria-label="Copy code">
          <svg class="copy-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          <svg class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: none;">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </button>
      </div>
      ${body}
    </div>
  `;
}

async function renderMarkdown(content: string): Promise<{
  html: string;
  toc: TableOfContentsItem[];
}> {
  const toc: TableOfContentsItem[] = [];
  const slugger = new Slugger();
  let isFirstH1 = true;

  const shiki = await getShikiHighlighter();
  await loadLanguagesFor(content, shiki);
  const loadedLangs = new Set(shiki.getLoadedLanguages());

  const renderer = new marked.Renderer();

  renderer.heading = function (text, level) {
    // The first H1 duplicates the frontmatter title rendered by the page.
    if (level === 1 && isFirstH1) {
      isFirstH1 = false;
      return '';
    }

    if (level > 3) return `<h${level}>${text}</h${level}>`;

    const cleanText = text
      .replace(/<[^>]*>/g, '')
      .replace(/\$([^$]+)\$/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/&[^;]+;/g, '')
      .trim();

    const id = slugger.slug(cleanText);
    toc.push({ id, text: cleanText, level });
    return `<h${level} id="${id}">${text}</h${level}>`;
  };

  // Sizing and loading hints are applied afterwards by enhanceImages, which
  // also catches the raw <img> tags authors write directly in the markdown.
  renderer.image = function (href, title, text) {
    const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
    return `<img src="${escapeHtml(href ?? '')}" alt="${escapeHtml(text ?? '')}"${titleAttr} />`;
  };

  renderer.code = function (code, language) {
    const lang = language || 'text';
    const resolved = resolveLang(lang);
    try {
      const html = shiki.codeToHtml(code, {
        lang: resolved && loadedLangs.has(resolved) ? resolved : 'text',
        theme: SHIKI_THEME
      });
      return renderCodeBlock(lang, code, html);
    } catch (error) {
      console.error(`Shiki failed to highlight a "${lang}" block:`, error);
      const fallback = `<pre class="shiki"><code class="language-${escapeHtml(lang)}">${escapeHtml(code)}</code></pre>`;
      return renderCodeBlock(lang, code, fallback);
    }
  };

  return { html: enhanceImages(marked(content, { renderer })), toc };
}

/**
 * Adds intrinsic dimensions and loading hints to every image in the rendered
 * document, whether it came from markdown syntax or a raw <img> tag. Runs over
 * the final HTML so "first image" means first in document order.
 */
function enhanceImages(html: string): string {
  let isFirst = true;

  return html.replace(/<img\b([^>]*?)(\/?)>/gi, (tag, attrs: string, selfClose: string) => {
    const src = attrs.match(/\bsrc\s*=\s*["']([^"']*)["']/i)?.[1];
    const has = (name: string) => new RegExp(`\\b${name}\\s*=`, 'i').test(attrs);
    let extra = '';

    const size = src ? imageSizes[src] : undefined;
    if (size && !has('width') && !has('height')) {
      extra += ` width="${size.width}" height="${size.height}"`;
    }
    if (!has('decoding')) extra += ' decoding="async"';

    // The first image is usually the LCP element; lazy-loading it defers the
    // fetch until layout, which is exactly the wrong time.
    if (isFirst) {
      if (!has('fetchpriority')) extra += ' fetchpriority="high"';
      isFirst = false;
    } else if (!has('loading')) {
      extra += ' loading="lazy"';
    }

    return extra ? `<img${attrs}${extra}${selfClose}>` : tag;
  });
}

/** Strips markdown/HTML down to prose. Code blocks are dropped entirely. */
function toPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface RawPost {
  meta: BlogPostMeta;
  body: string;
}

function parsePost(filepath: string, source: string): RawPost | null {
  const slug = filepath.replace(/^.*\/blog\/(.+)\.md$/, '$1');

  try {
    const { frontmatter, body } = parseFrontmatter(source);
    const date = String(frontmatter.date ?? '');

    if (Number.isNaN(new Date(date).getTime())) {
      console.error(`Skipping "${slug}": missing or unparseable date "${date}".`);
      return null;
    }

    const searchText = toPlainText(body);
    const wordCount = searchText.split(/\s+/).filter(Boolean).length;

    return {
      body,
      meta: {
        slug,
        date,
        title: String(frontmatter.title ?? slug),
        tags: Array.isArray(frontmatter.tags) ? frontmatter.tags : [],
        excerpt: String(frontmatter.description ?? `${searchText.slice(0, 160)}...`),
        readingTime: `${Math.max(1, Math.ceil(wordCount / 200))} min read`,
        featured: frontmatter.featured === true,
        searchText
      }
    };
  } catch (error) {
    // One malformed file should cost that post, not the whole blog.
    console.error(`Skipping "${slug}": failed to parse.`, error);
    return null;
  }
}

let rawPosts: Map<string, RawPost> | null = null;

function getRawPosts(): Map<string, RawPost> {
  if (rawPosts) return rawPosts;

  const parsed = Object.entries(blogModules)
    .map(([filepath, source]) => parsePost(filepath, source))
    .filter((post): post is RawPost => post !== null)
    .filter(post => new Date(post.meta.date) <= new Date())
    .sort((a, b) => new Date(b.meta.date).getTime() - new Date(a.meta.date).getTime());

  rawPosts = new Map(parsed.map(post => [post.meta.slug, post]));
  return rawPosts;
}

/** Newest first. `featured` is presentation only and does not affect order. */
export function getAllPosts(): BlogPostMeta[] {
  return [...getRawPosts().values()].map(post => post.meta);
}

const renderedPosts = new Map<string, BlogPost>();

export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  const cached = renderedPosts.get(slug);
  if (cached) return cached;

  const raw = getRawPosts().get(slug);
  if (!raw) return null;

  const { html, toc } = await renderMarkdown(raw.body);
  const post: BlogPost = { ...raw.meta, content: html, tableOfContents: toc };
  renderedPosts.set(slug, post);
  return post;
}
